/**
 * Pure formatting/derivation logic for the OBS-5 market-view panel (§A.4),
 * factored out of `MarketPanel.ts` so the schema-parsing/staleness/empty-
 * state rules are unit-testable without touching the DOM (mirrors the
 * project's existing convention — `src/brain/lod.ts`, `src/brain/palette.ts`
 * — of keeping rendering logic thin over a pure, tested core).
 */

import type { MarketDocument, MarketQuote, MarketSlot } from "../types/incremental";
import { formatPrice, priceDecimals } from "../format/number";

export type MarketPanelState =
  | { readonly kind: "empty" }
  | { readonly kind: "ready"; readonly asOfSeq: number; readonly slots: readonly MarketSlot[] };

/** Decides whether the panel has anything to draw. A `null` document (the
 * ref itself absent — an unmigrated role, or a v2 role whose publisher
 * hasn't folded a `market` object yet) and a document with zero slots (the
 * real, honest shape a fresh role publishes before its first `market.view`,
 * per `docs/OBSERVATORY_TELEMETRY.md`) are both the same user-facing state:
 * nothing to show yet, never a fabricated slot. */
export function buildMarketPanelState(document: MarketDocument | null): MarketPanelState {
  if (!document || document.slots.length === 0) return { kind: "empty" };
  return { kind: "ready", asOfSeq: document.as_of_seq, slots: document.slots };
}

/** `true` for a market that trades continuously (crypto, §A.4/CPU §10) or is
 * currently in its regular session — the only states OBS-5's contract says
 * should read as "live" rather than dimmed. */
export function isLiveSession(sessionState: string): boolean {
  return sessionState === "regular";
}

const SESSION_LABELS: Readonly<Record<string, string>> = {
  regular: "Regular session",
  after_hours: "After hours",
  premarket: "Pre-market",
  pre_market: "Pre-market",
  overnight: "Overnight",
  closed: "Closed",
};

/** Plain-language session label (orchestrator course correction,
 * 2026-09-28: "a small session pill in plain words") — real enum values
 * verified against the fixture (`session_state`: "regular"/"after_hours"/
 * "premarket"). An unrecognized value degrades to a title-cased version of
 * itself, never a blank pill — the exact enum string is still available in
 * the pill's tooltip for anyone who wants it verbatim. */
export function sessionStateLabel(sessionState: string): string {
  const known = SESSION_LABELS[sessionState];
  if (known) return known;
  const words = sessionState.replace(/_/g, " ").trim().split(/\s+/);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function midpoint(bid: string, ask: string): number | null {
  const b = Number.parseFloat(bid);
  const a = Number.parseFloat(ask);
  if (!Number.isFinite(b) || !Number.isFinite(a)) return null;
  return (a + b) / 2;
}

/** The quote legs at full precision when the publisher recorded them (BEN's
 * `bid_exact`/`ask_exact`), else the cent-rounded display legs. OQA: the panel
 * read only the cent fields, so ADA (bid 0.2503, ask 0.251001) showed
 * "0.25 / 0.25 · spread 0.00". */
export function exactQuote(quote: MarketQuote | null): {
  readonly bid: string | null;
  readonly ask: string | null;
} {
  return {
    bid: positiveLeg(quote?.bid_exact ?? quote?.bid ?? null),
    ask: positiveLeg(quote?.ask_exact ?? quote?.ask ?? null),
  };
}

/** A quote leg of 0 is an ABSENT side, not a price: the live male's after-hours IEX
 * IWM quote was bid 281.39 / ask 0.00, which the card turned into a 140.69 "last
 * price" and a -281.39 spread (OQA). */
function positiveLeg(leg: string | null): string | null {
  if (leg === null) return null;
  const n = Number.parseFloat(leg);
  return Number.isFinite(n) && n > 0 ? leg : null;
}

/** Below this, a cent-rounded trade print loses real precision (a $0.2503 coin
 * prints "0.25"): the exact quote mid is the more faithful headline. */
const SUB_TEN_DOLLARS = 10;

/** The "last price" a card leads with: prefer the last real trade print
 * (the most literal "what did it last trade at"), fall back to the
 * bid/ask midpoint when both legs of a quote are present, then a single
 * known leg, and finally an honest "no quote yet" — never a fabricated
 * price. */
export function lastPriceText(slot: MarketSlot): {
  readonly text: string;
  readonly known: boolean;
} {
  const quote = exactQuote(slot.latest_quote);
  const exactMid = quote.bid && quote.ask ? midpoint(quote.bid, quote.ask) : null;
  if (slot.last_trade) {
    if (slot.last_trade.price_exact) {
      return { text: formatPrice(slot.last_trade.price_exact), known: true };
    }
    const printed = Number.parseFloat(slot.last_trade.price);
    const exactLegs = Boolean(slot.latest_quote?.bid_exact && slot.latest_quote.ask_exact);
    if (exactLegs && exactMid !== null && Number.isFinite(printed) && printed < SUB_TEN_DOLLARS) {
      return { text: formatPrice(exactMid), known: true };
    }
    return { text: formatPrice(slot.last_trade.price), known: true };
  }
  if (exactMid !== null) return { text: formatPrice(exactMid), known: true };
  if (quote.bid) return { text: `${formatPrice(quote.bid)} bid`, known: true };
  if (quote.ask) return { text: `${formatPrice(quote.ask)} ask`, known: true };
  return { text: "no quote yet", known: false };
}

/** `"150.00 / 150.05\nspread 0.05"`-style text (bid/ask line, then the spread), or `null` when either leg
 * is missing (never a fabricated spread from a partial quote). */
export function quoteSpreadText(rawQuote: MarketQuote | null): string | null {
  const quote = exactQuote(rawQuote);
  if (!quote.bid || !quote.ask) return null;
  const bid = Number.parseFloat(quote.bid);
  const ask = Number.parseFloat(quote.ask);
  if (!Number.isFinite(bid) || !Number.isFinite(ask)) return null;
  if (ask < bid) return `${formatPrice(quote.bid)} / ${formatPrice(quote.ask)}\ncrossed`;
  // The spread at the precision of the price it is quoted against.
  const spread = spreadText(ask - bid, bid);
  return `${formatPrice(quote.bid)} / ${formatPrice(quote.ask)}\nspread ${spread}`;
}

/** The spread at the precision of the price it is quoted against (`priceDecimals`),
 * trailing zeros trimmed to at least 2 places. */
function spreadText(spread: number, price: number): string {
  const fixed = spread.toFixed(priceDecimals(Math.abs(price)));
  return fixed.replace(/(\.\d\d\d*?)0+$/, "$1");
}

/** A short, honest staleness description. Mirrors the contract's own
 * convention (`docs/OBSERVATORY_TELEMETRY.md` "Market view (OBS-5)"):
 * `gap` and `staleness` are independent fields, read together, never derived
 * from one another — a feed can be flagged `gap` with `staleness` still 0.0
 * (detected this cycle, not yet aged) or have climbing `staleness` with no
 * `gap` (a replay quote aging toward the ceiling). */
export function stalenessText(slot: MarketSlot): string {
  if (slot.gap) return "gap detected";
  if (slot.staleness <= 0) return "fresh";
  if (slot.staleness <= 1) return `stale (${slot.staleness.toFixed(1)}x)`;
  return `stale — over ceiling (${slot.staleness.toFixed(1)}x)`;
}

/** CSS state for `MarketPanel.ts`'s staleness dot — a separate concern from
 * `stalenessText` above (2026-09-28 fix, verified live): an overnight/
 * closed symbol with no quote at all yet (`latest_quote`/`last_trade` both
 * null) still reports `staleness: 0, gap: false`, which `stalenessText`
 * correctly and honestly calls "fresh" (nothing has been flagged stale,
 * because the underlying fields really are that value) — but painting that
 * as the same green dot used for "a real, current quote" reads as a
 * contradiction next to the tile's own "no quote yet" text, i.e. looks
 * broken rather than intentionally quiet. `priceKnown` is `lastPriceText(
 * slot).known` — the exact same signal the "no quote yet" text already uses,
 * so the dot and the text can never disagree. A real flagged `gap` still
 * wins over "unknown" — a genuine data-gap problem stays visibly red even
 * if no quote has landed since it was flagged.
 *
 * Four tiers, not three (code-review finding): staleness over the 1.0x
 * ceiling is materially worse than a quote merely aging toward it —
 * `stalenessText` already says so in words ("stale — over ceiling") — but
 * the dot used to paint both the same amber "warn", so an operator
 * scanning dots for the worst-offending feed couldn't tell them apart at a
 * glance. Over-ceiling reuses the same red as `gap`: both are genuine
 * problems, distinguished by the tooltip's own wording (`stalenessText`),
 * not by inventing a fifth colour outside the closed palette. */
export function stalenessDotClass(slot: MarketSlot, priceKnown: boolean): string {
  if (slot.gap) return "obs-market-staleness-gap";
  if (!priceKnown) return "obs-market-staleness-unknown";
  if (slot.staleness > 1) return "obs-market-staleness-critical";
  if (slot.staleness > 0) return "obs-market-staleness-warn";
  return "obs-market-staleness-fresh";
}

/** `true` when this slot's payload actually carries L2 depth to draw a
 * ladder for (crypto feeds only in practice, §A.4) — otherwise the panel
 * shows `ladder_reason` as a short note instead. */
export function hasLadder(slot: MarketSlot): boolean {
  return Boolean(slot.ladder && (slot.ladder.bids.length > 0 || slot.ladder.asks.length > 0));
}

/** `"AAPL, MSFT, QQQ, SPY"`-style declared-universe summary, sorted (the
 * publisher already sorts `slots` by symbol, but sort again defensively —
 * this must never depend on fetch/object-key ordering). */
export function declaredUniverseText(slots: readonly MarketSlot[]): string {
  return slots
    .map((slot) => slot.symbol)
    .slice()
    .sort()
    .join(", ");
}

/** The `ladder_reason` shared by every ladder-less slot, or `null` when they
 * differ (or none apply) — owner requirement 2026-09-27 (polish round):
 * "the market cards' `IEX top-of-book only` note repeats in every card —
 * show it once in the panel header line instead." A universe is
 * overwhelmingly one feed (all-equity or all-crypto today), so this is
 * almost always non-null in practice; a genuinely mixed universe (some
 * slots with a real ladder, ladder-less ones citing different reasons)
 * falls back to `null` so `MarketPanel` keeps the honest per-card note
 * instead of hiding a real difference. */
export function commonLadderReason(slots: readonly MarketSlot[]): string | null {
  const reasons = new Set(
    slots
      .filter((slot) => !hasLadder(slot) && slot.ladder_reason !== null)
      .map((slot) => slot.ladder_reason),
  );
  if (reasons.size !== 1) return null;
  const [reason] = reasons;
  return reason ?? null;
}
